"use operator overloading";

/**
 * Stores a single scalar value and its gradient for automatic differentiation
 */
export class Value {
  grad: number;
  #backward: () => void;
  #prev: Set<Value>;

  constructor(
    public data: number,
    children: Value[] | Set<Value> = [],
    public _op: string = "",
  ) {
    this.grad = 0;
    this.#backward = () => {};
    this.#prev = new Set(children);
  }

  add(other: Value | number): Value {
    const otherValue = other instanceof Value ? other : new Value(other);
    const out = new Value(this.data + otherValue.data, [this, otherValue], "+");

    out.#backward = () => {
      this.grad += out.grad;
      otherValue.grad += out.grad;
    };

    return out;
  }

  [Symbol.for("+")](other: Value | number): Value {
    return this.add(other);
  }

  mul(other: Value | number): Value {
    const otherValue = other instanceof Value ? other : new Value(other);
    const out = new Value(this.data * otherValue.data, [this, otherValue], "*");

    out.#backward = () => {
      this.grad += otherValue.data * out.grad;
      otherValue.grad += this.data * out.grad;
    };

    return out;
  }

  [Symbol.for("*")](other: Value | number): Value {
    return this.mul(other);
  }

  pow(other: number): Value {
    if (typeof other !== "number") {
      throw new Error("only supporting number powers for now");
    }
    const out = new Value(this.data ** other, [this], `**${other}`);

    out.#backward = () => {
      this.grad += other * this.data ** (other - 1) * out.grad;
    };

    return out;
  }

  [Symbol.for("**")](other: number): Value {
    return this.pow(other);
  }

  neg(): Value {
    return this.mul(-1);
  }

  [Symbol.for("minus")](): Value {
    return this.neg();
  }

  sub(other: Value | number): Value {
    const otherValue = other instanceof Value ? other : new Value(other);
    return this.add(otherValue.neg());
  }

  [Symbol.for("-")](other: Value | number): Value {
    return this.sub(other);
  }

  div(other: Value | number): Value {
    const otherValue = other instanceof Value ? other : new Value(other);
    return this.mul(otherValue.pow(-1));
  }

  [Symbol.for("/")](other: Value | number): Value {
    return this.div(other);
  }

  relu(): Value {
    const out = new Value(this.data < 0 ? 0 : this.data, [this], "ReLU");

    out.#backward = () => {
      this.grad += (out.data > 0 ? 1 : 0) * out.grad;
    };

    return out;
  }

  backward(): void {
    // topological order all of the children in the graph
    const topo: Value[] = [];
    const visited = new Set<Value>();

    const buildTopo = (v: Value) => {
      if (!visited.has(v)) {
        visited.add(v);
        v.#prev.forEach((child) => {
          buildTopo(child);
        });
        topo.push(v);
      }
    };

    buildTopo(this);

    // go one variable at a time and apply the chain rule to get its gradient
    this.grad = 1;
    for (let i = topo.length - 1; i >= 0; i--) {
      topo[i].#backward();
    }
  }

  toString(): string {
    return `Value(data=${this.data}, grad=${this.grad})`;
  }
}
